/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Spin,
  Tabs,
  Card,
  message,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import percentFormatter from 'utils/percentFormatter';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import ModalDelete from 'components/ModalDelete';
import { FLOAT_NUMER_PATTERN } from 'utils/patterns';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getTarifasISN,
  postTarifaISN,
  deleteTarifaISN,
  patchTarifaISN,
  getTarifaISN,
} from 'api/nominas/tarifasISN';
import { checkToken } from 'api/utils';
import TabPane from 'antd/lib/tabs/TabPane';
import { makeOnChangeTabKey } from 'utils/tabsManagement';
import makeMessager from 'utils/complexFormsMessages';
import { getModelPermission } from 'utils/permission';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
  ESTATUS_INACTIVO,
} from 'utils/constants';
import { CheckCircleOutlined } from '@ant-design/icons';
import ModalConfirmation from 'components/ModalConfirmation';
import TarifasISNTasas from './TarifasISNTasas';
import TarifasISNCuentasContables, { cuentaContableISNPermission } from './TarifasISNCuentasContables';

export const tarifasISNPermission = {
  permissionModel: 'tarifaisn',
  catalogsModels: [],
  tabsModels: [],
};

export const onErrorFields = (
  err,
  setLoading = null,
  formsInstances = [],
  setCurrentTabKey,
) => {
  if (typeof setLoading === 'function') {
    setLoading(false);
  }
  const errorKeys = err.errorFields.map((e) => e.name[0]);
  const formWithErrors = formsInstances.find(
    (obj) => obj.tabFields.some(
      (val) => errorKeys.includes(val),
    ),
  );
  if (formWithErrors) {
    setTimeout(() => {
      if (typeof setCurrentTabKey === 'function') {
        setCurrentTabKey(formWithErrors.tabKey);
      }
    });
  }
};

const TarifaISN = ({ permission }) => {
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('tasas_isn');
  const [mutable, setMutable] = useState(true);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [tarifaId, setTarifaId] = useState();
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleInactivo, setVisibleInactivo] = useState(false);
  const [disableEditar, setDisableEditar] = useState(false);

  const getPermission = getModelPermission(
    cuentaContableISNPermission.permissionModel,
    cuentaContableISNPermission.catalogsModels,
    cuentaContableISNPermission.tabsModels,
    false,
  );

  const requiredFormStructure = [
    {
      parentTabKey: 'tasas_isn',
      tabKey: 'tasas_isn',
    },
    {
      parentTabKey: 'cuentas_contables',
      tabKey: 'cuentas_contables',
    },
  ];

  const basicKeys = [
    'tasas_isn',
    'cuentas_contables',
  ];

  const complexForms = [];

  const formInstances = [
    form,
    formDatos,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTarifasISN();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _periodos,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
        getTarifasISN(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setPeriodos(_periodos);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    setMutable(true);
    setTarifaId();
    setCurrentTabKey('tasas_isn');
    form.resetFields();
    formDatos.resetFields();
    setDisableEditar(false);
    setVisibleInactivo(false);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    setTarifaId(match.id);
    const _tarifa = await getTarifaISN(match.id);
    setTimeout(() => {
      if (_tarifa) {
        setDisableEditar(true);
        // eslint-disable-next-line no-unused-expressions
        _tarifa?.estados_globales !== ESTATUS_ACTIVO ? setDisableEditar(true) : setDisableEditar(false);
        form.resetFields();
        formDatos.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_tarifa));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = true) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDatos.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formDatos.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchTarifaISN(key, values) : postTarifaISN(values));
      if (saved) {
        onSuccess({}, ` ${selectedRowKeys ? 'Editado' : 'Agregado'} exitosamente.`);
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          await onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      if (err.errorFields) {
        onErrorFields(
          err,
          setLoading,
          requiredFormStructure,
          setCurrentTabKey,
        );
        message.warn('Favor de corregir los errores señalados');
      } else {
        onError(
          err,
          setLoading,
          formInstances,
        );
      }
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteTarifaISN(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    if (!record?.fecha_final) {
      form.setFieldValue('fecha_final', null);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setCurrentTabKey('tasas_isn');
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const showMsg = makeMessager(setCurrentTabKey);

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys,
    complexForms,
    currentTabKey,
    setCurrentTabKey,
    showMsg,
  );

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar una Tasa ISN ya autorizada');
      }
      const vals = {
        ...form.getFieldsValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchTarifaISN(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setVisibleAutorizar(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmationEstatus = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede cancelar una Tarifa ISN ya cancelada');
      }
      const vals = {
        ...form.getFieldsValue(),
        estados_globales: ESTATUS_CANCELADO,
      };
      const res = await patchTarifaISN(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setVisibleCancelar();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      width: 200,
      render: (val) => val?.format(),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 200,
      render: (val) => val?.format(),
    },
    {
      titleText: 'Porcentaje',
      dataIndex: 'porcentaje',
      key: 'porcentaje',
      width: 200,
      align: 'right',
      render: (val) => percentFormatter(val || 0),
    },
    {
      titleText: 'Ejercicio',
      dataIndex: 'ejercicio',
      key: 'ejercicio',
      width: 200,
      render: (val) => periodos.find((e) => e.id === val)?.periodo,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];
  const handleContentPrint = () => {
    const fechaInicial = new Date(form.getFieldValue('fecha_inicial'));
    const fechaFinal = form.getFieldValue('fecha_final') ? new Date(form.getFieldValue('fecha_final')) : null;
    if (!fechaFinal) {
      return `Tasa de ISN del: ${fechaInicial.toLocaleDateString()}`;
    }
    return `Tasa de ISN del: ${fechaInicial.toLocaleDateString()} al: ${fechaFinal.toLocaleDateString()}`;
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
    startDate: [
      { required: true },
      {
        validator: async () => {
          const { fecha_inicial, fecha_final } = form.getFieldsValue();
          if (fecha_inicial && fecha_final
            && fecha_inicial.diff(fecha_final, 'days') >= 0) {
            throw new Error('Este campo debe ser menor que la Fecha Final');
          }
        },
      },
    ],
    endDate: [
      {
        validator: async () => {
          const { fecha_inicial, fecha_final } = form.getFieldsValue();
          if (fecha_inicial && fecha_final
            && fecha_final.diff(fecha_inicial, 'days') <= 0) {
            throw new Error('Este campo debe ser mayor que la Fecha Inicial');
          }
        },
      },
    ],
    valor: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
    ],
    fecha_inicial: [
      requiredRule,
      {
        validator: async () => {
          const { ejercicio, fecha_inicial } = form.getFieldsValue();
          const _periodo = periodos.find((e) => e.id === ejercicio);
          if (_periodo?.fecha_inicial > fecha_inicial) {
            throw new Error('La Fecha Inicial debe ser mayor o igual a la fecha inicial del ejercicio');
          }
          if (fecha_inicial > _periodo?.fecha_final) {
            throw new Error('La Fecha Inicial no puede ser mayor que la fecha final del ejercicio');
          }
        },
      },
    ],
    fecha_final: [
      {
        validator: async () => {
          const { ejercicio, fecha_final, fecha_inicial } = form.getFieldsValue();
          const _periodo = periodos.find((e) => e.id === ejercicio);
          const fechaFin = fecha_final?.set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
          });
          if (fecha_final) {
            if (fechaFin > _periodo?.fecha_final) {
              throw new Error('La Fecha Final debe ser menor o igual a la fecha final del ejercicio');
            }
            if (fecha_final < _periodo?.fecha_inicial) {
              throw new Error('La Fecha Final debe ser mayor a la fecha inicial del ejercicio');
            }
            if (fecha_final < fecha_inicial) {
              throw new Error('La Fecha Final debe ser mayor o igual a la fecha inicial');
            }
          }
        },
      },
    ],
    porcentaje: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
      {
        validator: async () => {
          const { porcentaje } = form.getFieldValue();
          if (porcentaje > 100) {
            throw new Error('Este campo no puede ser mayor a 100%');
          }
        },
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            disableDelete={
              data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
              || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO
            }
            customActions={[
              {
                onClick: () => setVisibleAutorizar(true),
                permission: { model: 'tarifaisn', action: 'autorizar' },
                text: 'Autorizar',
                icon: <CheckCircleOutlined />,
                disabledBySelection: false,
                className: 'success',
                disabled: !selectedRowKeys?.length
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_INACTIVO,
              },
              {
                permission: { model: 'tarifaisn', action: 'cancelar' },
                onClick: () => setVisibleCancelar(true),
                text: 'Cancelar',
                icon: <CheckCircleOutlined />,
                disabledBySelection: false,
                className: 'cancel',
                disabled: !selectedRowKeys?.length
                  || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO,
              },
            ]}
            permission={permission}
            allowExportPDF={false}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tasa de ISN`}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                onFinishText="Guardar y Continuar"
              />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFIrstError
              name="general"
            >
              <Tabs
                activeKey={currentTabKey}
                onChange={mutable ? onChangeTabKey : setCurrentTabKey}
                type="card"
              >
                <TabPane
                  tab="Tasa ISN"
                  key="tasas_isn"
                  forceRender
                >
                  <Row gutter={10}>
                    <Col xs={24} sm={24} md={24}>
                      <TarifasISNTasas
                        form={form}
                        rules={rules}
                        periodos={periodos}
                        estados={estados}
                        selectedRowKeys={selectedRowKeys}
                        disableEditar={disableEditar}
                        setVisibleInactivo={setVisibleInactivo}
                      />
                    </Col>
                  </Row>
                </TabPane>
                <TabPane
                  tab="Cuentas Contables"
                  key="cuentas_contables"
                  forceRender
                  disabled={!selectedRowKeys.length}
                >
                  <TarifasISNCuentasContables
                    tarifaId={tarifaId}
                    rules={rules}
                    permission={getPermission}
                    disableEditar={disableEditar}
                  />
                </TabPane>
              </Tabs>
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={handleContentPrint()}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setVisibleAutorizar(false);
          }}
          open={visibleAutorizar}
          contentTitle={`Autorizar la ${handleContentPrint()}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationEstatus}
          onFinishText="Continuar"
          onCancel={() => {
            setVisibleCancelar(false);
          }}
          open={visibleCancelar}
          title="Confirmación"
          contentTitle={`Cancelar la ${handleContentPrint()}`}
          subMessage="Esta acción no podrá revertirse"
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleInactivo(false);
          }}
          contentTitle={`Inactivar la ${handleContentPrint()}`}
          open={visibleInactivo}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TarifaISN;
